/**
 * Email-verification hook for Aegix One.
 *
 * ADR-118 #18: the lib's `LoginAdapter` intentionally does
 * not model email verification (it's a one-shot side flow, not part
 * of the auth-state machine). After a successful verify the backend
 * sets the allauth `sessionid` cookie and — via Aegix's
 * `JWTTokenStrategy` — returns a JWT in `meta.access_token` and the
 * user shape in `data.user`.
 *
 * Bridging into the auth-identity state machine: write the JWT + user
 * into `@aegix/auth-core`'s versioned storage, then navigate to `/`
 * with a full reload. The reload remounts `<AuthProvider>`, whose
 * boot-time reconcile reads the freshly-persisted
 * storage and lands on `authenticated`. This is the same pattern
 * `AuthCallback.tsx` uses for OAuth completion.
 */

import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  createLocalStorageAdapter,
  StorageKeys,
} from '@aegix/auth-core';
import type { AllauthUser } from '@aegix/auth-identity';
import { verifyEmail } from '../lib/allauth-endpoints';

const storage = createLocalStorageAdapter();

export function useVerifyEmail() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const emailFromState = (location.state as { email?: string })?.email;
  const verificationKey = searchParams.get('key');

  const [status, setStatus] = useState<'pending' | 'verifying' | 'verified' | 'error'>(
    verificationKey ? 'verifying' : 'pending'
  );
  const [error, setError] = useState('');

  useEffect(() => {
    if (!verificationKey) return;

    verifyEmail(verificationKey)
      .then((response) => {
        const token = response.meta?.access_token;
        const user = response.data?.user as AllauthUser | undefined;
        if (token && user) {
          // Persist into the lib's keys and navigate with a full
          // reload so the provider re-reconciles. setStatus before
          // navigating so the UI flashes the success state during
          // the brief redirect.
          storage.set(StorageKeys.token, token);
          storage.set(StorageKeys.user, user);
          setStatus('verified');
          window.location.href = '/';
          return;
        }
        // Verified but no token (rare) — show success but the user
        // will need to log in manually.
        setStatus('verified');
      })
      .catch((err) => {
        setError(
          err instanceof Error
            ? err.message
            : 'Verification failed. The link may have expired.',
        );
        setStatus('error');
      });
  }, [verificationKey]);

  return { emailFromState, status, error };
}
